import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api';
import { useStepUp, type StepUpPurpose } from '@/lib/api/auth';
import { cn } from '@/lib/utils';

const CELLS = 6;
const EMPTY: string[] = Array.from({ length: CELLS }, () => '');

export interface StepUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Зачем спрашиваем код — backend привязывает выданный токен к этой операции. */
  purpose: StepUpPurpose;
  /**
   * Для `switch_account` — аккаунт, В который переключаемся: проверяется 2FA
   * именно его, а не текущего пользователя.
   */
  targetUserId?: string;
  /** Почта аккаунта — отдельной строкой, чтобы было видно, чей код спрашиваем. */
  accountLabel?: string | null;
  /** Успех: короткоживущий токен подтверждения. Диалог закрывает вызывающий. */
  onConfirmed: (stepUpToken: string) => void;
  /**
   * Идёт шаг, ради которого спрашивали код (например само переключение
   * аккаунта). Диалог остаётся открытым и не даёт себя закрыть: человек
   * смотрит именно сюда, и «нажал — и ничего» здесь недопустимо.
   */
  busy?: boolean;
  /** Подпись кнопки, пока идёт внешний шаг. */
  busyLabel?: string;
  /**
   * Ошибка внешнего шага (код был верный, а дальше не получилось). Показываем
   * в этом же диалоге — иначе она уходит в размонтированное меню и человек не
   * видит ничего.
   */
  externalError?: string | null;
}

/**
 * Подтверждение личности кодом из приложения-аутентификатора (AK-469).
 *
 * Появляется только на проде — на staging backend этот шаг не требует
 * (см. `/auth/switch-account`), поэтому диалог просто не открывается.
 *
 * Формулировки зафиксированы founder'ом на wireframe (PR #569, экран 2):
 * «Подтвердите вход» / «Введите код из приложения-аутентификатора» /
 * «Неверный код». Слов «step-up», «TOTP», «2FA» в интерфейсе нет — это
 * термины модели, а не язык пользователя.
 */
export function StepUpDialog({
  open,
  onOpenChange,
  purpose,
  targetUserId,
  accountLabel,
  onConfirmed,
  busy = false,
  busyLabel = 'Продолжаем…',
  externalError = null,
}: StepUpDialogProps) {
  const stepUp = useStepUp();
  const [digits, setDigits] = useState<string[]>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const cellsRef = useRef<Array<HTMLInputElement | null>>([]);
  // Какой код уже отправляли — страховка от повторной автоотправки того же
  // кода при каждом ре-рендере (эффект ниже смотрит на длину строки).
  const sentRef = useRef<string | null>(null);

  const code = digits.join('');
  // Ошибка внешнего шага важнее внутренней: код-то был верный, человеку нужно
  // знать, что не получилось дальше.
  const shownError = externalError ?? error;

  // Сброс при каждом открытии: старый код и старая ошибка в новом диалоге
  // выглядят как баг.
  useEffect(() => {
    if (open) {
      setDigits(EMPTY);
      setError(null);
      sentRef.current = null;
    }
  }, [open]);

  const focusCell = (index: number) => {
    cellsRef.current[Math.max(0, Math.min(CELLS - 1, index))]?.focus();
  };

  // Внешний шаг упал — освобождаем поле под следующий код: в ячейках остался
  // бы уже потраченный.
  //
  // `sentRef` при этом НЕ сбрасываем. Эффекты одного коммита выполняются по
  // порядку объявления, а `digits` к моменту следующего (автоотправка) ещё
  // старые: обнулённый `sentRef` заставил бы отправить тот же код заново →
  // новый токен → новая неудача → снова сюда, и так до бесконечности (ловил
  // это на зависшем тесте). Поле пустеет само, а следующий код будет другим.
  useEffect(() => {
    if (externalError) {
      setDigits(EMPTY);
      cellsRef.current[0]?.focus();
    }
  }, [externalError]);

  const submit = useCallback(
    async (value: string) => {
      if (value.length < CELLS || stepUp.isPending || busy) return;
      sentRef.current = value;
      setError(null);
      try {
        const result = await stepUp.mutateAsync({
          code: value,
          purpose,
          target_user_id: targetUserId,
        });
        onConfirmed(result.step_up_token);
      } catch (err) {
        // 400 от backend'а — именно неверный код; 429 приходит со своим
        // текстом про лимит попыток, его показываем как есть.
        setError(
          err instanceof ApiError
            ? err.status === 400
              ? 'Неверный код'
              : err.detail || 'Не удалось проверить код'
            : 'Не удалось проверить код',
        );
        setDigits(EMPTY);
        sentRef.current = null;
        focusCell(0);
      }
    },
    [busy, onConfirmed, purpose, stepUp, targetUserId],
  );

  // Автоотправка после шестой цифры — кнопка остаётся для клавиатуры и
  // для повтора после ошибки.
  useEffect(() => {
    if (code.length === CELLS && sentRef.current !== code) {
      void submit(code);
    }
  }, [code, submit]);

  const writeDigits = (start: number, raw: string) => {
    const clean = raw.replace(/\D/g, '');
    if (!clean) return;
    setDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < clean.length && start + i < CELLS; i += 1) {
        next[start + i] = clean[i];
      }
      return next;
    });
    focusCell(start + clean.length);
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      setDigits((prev) => {
        const next = [...prev];
        if (next[index]) {
          next[index] = '';
        } else if (index > 0) {
          next[index - 1] = '';
        }
        return next;
      });
      if (!digits[index] && index > 0) focusCell(index - 1);
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusCell(index - 1);
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusCell(index + 1);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Пока идёт шаг после кода — закрывать нечем: Esc или клик мимо
        // оставили бы человека без единого следа происходящего.
        if (busy && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Подтвердите вход</DialogTitle>
          {/* Формулировка зафиксирована founder'ом — не дописывать к ней
              имя аккаунта, оно идёт отдельной строкой ниже. */}
          <DialogDescription>
            Введите код из приложения-аутентификатора
          </DialogDescription>
        </DialogHeader>

        {accountLabel && (
          <p className="-mt-2 truncate text-sm font-medium text-foreground">
            {accountLabel}
          </p>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit(code);
          }}
          className="space-y-3"
          autoComplete="off"
        >
          {/* Шесть отдельных ячеек — по wireframe (PR #569, экран 2).
              flex-wrap + min-w-0: на 320px-ширине диалог не разъезжается. */}
          <div
            role="group"
            aria-label="Код из приложения-аутентификатора"
            className="flex justify-center gap-1 sm:gap-2"
          >
            {digits.map((digit, index) => (
              <Input
                // Позиция цифры и есть её идентичность — список фиксированной
                // длины, перестановок не бывает.
                key={index}
                ref={(el) => {
                  cellsRef.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                aria-label={`Цифра ${index + 1}`}
                aria-invalid={!!shownError}
                autoFocus={index === 0}
                value={digit}
                disabled={stepUp.isPending || busy}
                onChange={(e) => writeDigits(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={(e) => {
                  e.preventDefault();
                  writeDigits(0, e.clipboardData.getData('text'));
                }}
                onFocus={(e) => e.target.select()}
                className={cn(
                  'h-11 w-10 shrink-0 px-0 text-center text-lg font-medium sm:w-11',
                  shownError && 'border-destructive',
                )}
              />
            ))}
          </div>

          {shownError && (
            <div className="space-y-1 text-center" role="alert">
              <p className="text-sm font-medium text-destructive">
                {shownError}
              </p>
              <p className="text-xs text-muted-foreground">
                {externalError
                  ? 'Код был принят. Попробуйте ещё раз — понадобится новый код.'
                  : 'Код обновляется каждые 30 секунд, попробуйте следующий'}
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={stepUp.isPending || busy}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              disabled={code.length < CELLS || stepUp.isPending || busy}
            >
              {busy
                ? busyLabel
                : stepUp.isPending
                  ? 'Проверяем…'
                  : 'Подтвердить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default StepUpDialog;
